import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Pre-delete audit dialog. Shows the admin every booking attributed to a
 * customer, broken down by payment channel (JIM, Cash, Stripe, Other) BEFORE
 * any data is wiped. The JIM and Cash totals are the ones the owner has to
 * reconcile externally (her JIM card processor + her cash drawer) — so she
 * always sees the dollars at risk before clicking "Delete everything".
 *
 * Defaults to the cascade (full purge) flow because the user repeatedly
 * indicated that's what she wants — but exposes a secondary "keep history"
 * button for the tax-trail-preservation case.
 */
export default function DeleteCustomerDialog({ user, open, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !user?.id) return;
    setPreview(null);
    setErr(null);
    axios.get(`${API}/admin/customers/${user.id}/delete-preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load preview"));
  }, [open, user?.id]);

  const run = async (cascade) => {
    if (!user?.id) return;
    setBusy(true);
    try {
      const url = `${API}/admin/customers/${user.id}${cascade ? "?cascade_bookings=true" : ""}`;
      const { data } = await axios.delete(url);
      toast.success(
        cascade
          ? `Deleted ${data.email || user.email || user.id} + ${data.deleted_bookings} booking${data.deleted_bookings === 1 ? "" : "s"}`
          : `Deleted ${data.email || user.email || user.id} (kept bookings for tax history)`
      );
      onDeleted?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !user) return null;

  const buckets = preview?.by_payment_method || {};
  const jim = buckets.jim_card || { count: 0, total: 0 };
  const cash = buckets.cash || { count: 0, total: 0 };
  const stripe = buckets.stripe || { count: 0, total: 0 };
  const other = buckets.other || { count: 0, total: 0 };
  const offlineTotal = jim.total + cash.total;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#121212] border-rose-500/30 text-white max-w-md" data-testid="delete-customer-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <span className="text-rose-400">⚠</span> Delete {preview?.user?.name || user.email}?
          </DialogTitle>
          <p className="text-xs text-zinc-400">
            This permanently removes the customer record. Use the breakdown below to reconcile any JIM / Cash fares first.
          </p>
        </DialogHeader>

        {!preview && !err && (
          <p className="text-sm text-zinc-500 py-6 text-center" data-testid="delete-preview-loading">Loading preview…</p>
        )}
        {err && (
          <p className="text-sm text-rose-400 py-4" data-testid="delete-preview-error">{err}</p>
        )}

        {preview && (
          <div className="space-y-3 py-2" data-testid="delete-preview-body">
            <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-zinc-400">Bookings on file</span>
                <span className="text-white font-bold">{preview.booking_count}</span>
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <span className="text-zinc-400">Total fares</span>
                <span className="text-white font-bold">${preview.total_fares.toFixed(2)}</span>
              </div>
              {preview.oldest_at && (
                <p className="text-[10px] text-zinc-500 mt-2">
                  {String(preview.oldest_at).slice(0, 10)} → {String(preview.newest_at).slice(0, 10)}
                </p>
              )}
            </div>

            {preview.booking_count > 0 && (
              <div className="space-y-1.5" data-testid="delete-preview-breakdown">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">
                  Reconcile these first
                </p>
                <PayRow label="JIM card" tone="amber" count={jim.count} total={jim.total} />
                <PayRow label="Cash"     tone="emerald" count={cash.count} total={cash.total} />
                <PayRow label="Stripe"   tone="zinc" count={stripe.count} total={stripe.total} />
                {other.count > 0 && <PayRow label="Other" tone="zinc" count={other.count} total={other.total} />}
                {offlineTotal > 0 && (
                  <p className="text-[11px] text-amber-300 mt-2 leading-relaxed" data-testid="delete-preview-reconcile-note">
                    ${offlineTotal.toFixed(2)} in JIM + Cash fares is attached. Make sure those are already in
                    your JIM dashboard / cash drawer before deleting — once you delete, that ledger entry won&apos;t link back to a customer.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="border-white/15 text-zinc-300 hover:bg-zinc-800"
            data-testid="delete-customer-cancel"
          >
            Cancel
          </Button>
          {preview?.booking_count > 0 && (
            <Button
              variant="outline"
              onClick={() => run(false)}
              disabled={busy || !preview}
              className="border-white/15 text-zinc-300 hover:bg-zinc-800"
              data-testid="delete-customer-keep-bookings"
              title="Delete the user but keep their booking history for the tax report"
            >
              Delete · keep history
            </Button>
          )}
          <Button
            onClick={() => run(true)}
            disabled={busy || !preview}
            className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
            data-testid="delete-customer-cascade"
          >
            {busy ? "Deleting…" : preview?.booking_count > 0
              ? `Delete everything · ${preview.booking_count} booking${preview.booking_count === 1 ? "" : "s"}`
              : "Delete user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayRow({ label, count, total, tone }) {
  const tones = {
    amber:   "text-amber-300",
    emerald: "text-emerald-300",
    zinc:    "text-zinc-300",
  };
  return (
    <div className="flex items-baseline justify-between text-[13px]" data-testid={`pay-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`${tones[tone] || tones.zinc} font-semibold`}>{label}</span>
      <span className="text-zinc-400 text-[11px] flex items-baseline gap-2">
        <span>{count} ride{count === 1 ? "" : "s"}</span>
        <span className="text-white font-mono font-bold tabular-nums">${total.toFixed(2)}</span>
      </span>
    </div>
  );
}
